import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api from './api/client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ToastContainer from './components/ui/ToastContainer';
import LandingPage from './pages/LandingPage';
import ProductPage from './pages/ProductPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import UserPanel from './pages/UserPanel';
import AdminPanel from './pages/AdminPanel';
import UserProfilePage from './pages/admin/UserProfilePage';
import OwnerPanel from './pages/OwnerPanel';
import TermsPage from './pages/TermsPage';
import RulesPage from './pages/RulesPage';
import PrivacyPage from './pages/PrivacyPage';
import NotFoundPage from './pages/NotFoundPage';
import DocsPage from './pages/DocsPage';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Csak bejelentkezett user számára
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

// Csak admin vagy owner számára - frontend guard (backend is véd!)
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'owner') return <Navigate to="/panel" replace />;
  return children;
}

// Csak owner számára
function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'owner') return <Navigate to="/panel" replace />;
  return children;
}

// Bejelentkezett user ne lássa a login/register oldalt
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return user ? <Navigate to="/panel" replace /> : children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (!p.startsWith('/owner') && !p.startsWith('/admin')) {
      api.post('/stats/pv', { page: p }).catch(() => {});
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/product/:id" element={<ProductPage />} />

          <Route path="/login" element={
            <GuestRoute><LoginPage /></GuestRoute>
          } />
          <Route path="/register" element={
            <GuestRoute><RegisterPage /></GuestRoute>
          } />
          <Route path="/forgot-password" element={
            <GuestRoute><ForgotPasswordPage /></GuestRoute>
          } />
          <Route path="/reset-password" element={
            <GuestRoute><ResetPasswordPage /></GuestRoute>
          } />
          <Route path="/verify-email" element={
            <GuestRoute><VerifyEmailPage /></GuestRoute>
          } />

          {/* Bejelentkezve is elérhető: a cserét a panelből indítja a user */}
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />

          <Route path="/panel" element={
            <PrivateRoute><UserPanel /></PrivateRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute><AdminPanel /></AdminRoute>
          } />
          <Route path="/admin/users/:id" element={
            <AdminRoute><UserProfilePage /></AdminRoute>
          } />
          <Route path="/owner" element={
            <OwnerRoute><OwnerPanel /></OwnerRoute>
          } />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <ToastContainer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
